import { TechnologyContributorSelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { Avatar } from '@components/shared/avatar.component';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useGetCompanyUsers } from '@queries/company.query';
import {
  useGetTechnologyContributors,
  useInnovatorAddTechnologyContributor,
  useInnovatorRemoveTechnologyContributor,
  useInnovatorUpdateTechnologyContributor,
} from '@queries/technology-contributors.query';
import { technologyContributorSchema } from '@schemas/technology-contributor.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { SelectOption } from '@utils/select.util';
import { useEffect, useMemo } from 'react';
import { TechnologyContributorStatusBadge } from './technology-contributor-status-badge.component';

type TechnologyContributorModalProps = {
  contributorToEdit: TechnologyContributorSelectDto | null;
  isOpen: boolean;
  onClose: () => void;
  technologyId: string;
};

export const TechnologyContributorModal: React.FC<TechnologyContributorModalProps> = (props) => {
  const { contributorToEdit, isOpen, onClose, technologyId } = props;

  const { data: companyUsers } = useGetCompanyUsers();
  const { data: existingContributors = [] } = useGetTechnologyContributors(technologyId);
  const { mutateAsync: addContributor, isPending: isAddPending } = useInnovatorAddTechnologyContributor();
  const { mutateAsync: updateContributor, isPending: isUpdatePending } = useInnovatorUpdateTechnologyContributor();
  const { mutateAsync: removeContributor, isPending: isDeletePending } = useInnovatorRemoveTechnologyContributor();

  const isEdit = Boolean(contributorToEdit);
  const isPending = isAddPending || isUpdatePending || isDeletePending;

  const getDefaultValues = () => ({
    contributionsEndDate: contributorToEdit?.contributionsEndDate || null,
    contributionsStartDate: contributorToEdit?.contributionsStartDate || null,
    userId: contributorToEdit?.userId || '',
  });

  const form = useAppForm({
    canSubmitWhenInvalid: true,
    defaultValues: getDefaultValues(),
    onSubmit: async ({ value }) => {
      const validationResult = technologyContributorSchema.safeParse(value);

      if (!validationResult.success) {
        const message = validationResult.error.issues[0]?.message;
        errorToast({ description: message || 'Please fill out all required fields.' });
        return;
      }

      try {
        if (isEdit && contributorToEdit) {
          await updateContributor({
            contributorId: contributorToEdit.id,
            contributionsEndDate: validationResult.data.contributionsEndDate,
            contributionsStartDate: validationResult.data.contributionsStartDate,
            technologyId,
            userId: validationResult.data.userId,
          });
          successToast({ description: 'Contributor updated successfully.' });
        } else {
          await addContributor({
            contributionsEndDate: validationResult.data.contributionsEndDate,
            contributionsStartDate: validationResult.data.contributionsStartDate,
            technologyId,
            userId: validationResult.data.userId,
          });
          successToast({ description: 'Contributor added successfully.' });
        }
        handleClose();
      } catch {
        errorToast({ description: 'Failed to save contributor.' });
      }
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: technologyContributorSchema,
    },
  });

  // Reset form when modal opens/closes or contributor changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: form methods are stable
  useEffect(() => {
    if (isOpen) {
      form.reset();
      form.setFieldValue('contributionsEndDate', contributorToEdit?.contributionsEndDate || null);
      form.setFieldValue('contributionsStartDate', contributorToEdit?.contributionsStartDate || null);
      form.setFieldValue('userId', contributorToEdit?.userId || '');
    }
  }, [isOpen, contributorToEdit]);

  // Filter out users who are already contributors (except current one in edit mode)
  const existingContributorUserIds = useMemo(() => {
    return new Set(existingContributors.map((c) => c.userId));
  }, [existingContributors]);

  const userOptions = useMemo(() => {
    if (!companyUsers?.users) return [];

    return companyUsers.users
      .filter((user) => {
        // Always show the user being edited
        if (contributorToEdit?.userId === user.id) return true;
        // Filter out existing contributors
        return !existingContributorUserIds.has(user.id);
      })
      .map((user) => ({
        label: user.profile ? `${user.profile.firstName} ${user.profile.lastName}` : user.email,
        value: user.id,
      }));
  }, [companyUsers, existingContributorUserIds, contributorToEdit]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleDelete = async () => {
    if (!contributorToEdit) return;

    try {
      await removeContributor({
        contributorId: contributorToEdit.id,
        technologyId,
      });
      successToast({ description: 'Contributor removed successfully.' });
      handleClose();
    } catch {
      errorToast({ description: 'Failed to remove contributor.' });
    }
  };

  const renderUserOption = (option: SelectOption) => {
    const user = companyUsers?.users?.find((u) => u.id === option.value);
    if (!user) return option.label;

    const fullName = user.profile ? `${user.profile.firstName} ${user.profile.lastName}` : user.email;

    return (
      <div className="flex items-center gap-2">
        <Avatar
          altText={fullName}
          imageUrl={user.avatarUrl}
          initialText={fullName}
          size="sm"
        />
        <div className="flex flex-col">
          <span className="text-sm font-medium">{fullName}</span>
          {user.profile?.title && <span className="text-xs text-gray-500">{user.profile.title}</span>}
        </div>
      </div>
    );
  };

  return (
    <Modal
      footer={{
        leftButton: isEdit
          ? {
              disabled: isPending,
              loading: isDeletePending,
              onClick: handleDelete,
              children: 'Delete',
              variant: 'destructive' as const,
            }
          : undefined,
        rightButton: {
          loading: form.state.isSubmitting,
          onClick: form.handleSubmit,
          children: isEdit ? 'Update' : 'Add',
        },
      }}
      header={{
        title: (
          <div className="flex items-center gap-2">
            <span>{isEdit ? 'Edit Key Contributor' : 'Add Key Contributor'}</span>
            {isEdit && (
              <form.Subscribe selector={(state) => state.values.contributionsEndDate}>
                {(contributionsEndDate) => (
                  <TechnologyContributorStatusBadge contributionsEndDate={contributionsEndDate} />
                )}
              </form.Subscribe>
            )}
          </div>
        ),
      }}
      isOpen={isOpen}
      onClose={handleClose}
      width="420px"
    >
      <form.AppForm>
        <div className="flex flex-col gap-4">
          <form.AppField name="userId">
            {(field) => (
              <field.SelectPopoverField
                allowClear={!isEdit}
                emptyMessage="No team members available"
                label="Team Member"
                options={userOptions}
                placeholder="Select a team member..."
                renderOption={renderUserOption}
                required
                searchPlaceholder="Search team members..."
              />
            )}
          </form.AppField>

          <form.AppField name="contributionsStartDate">
            {(field) => (
              <field.DatePickerField
                clearable
                label="Start Date"
                placeholder="Select start date..."
              />
            )}
          </form.AppField>

          <form.AppField name="contributionsEndDate">
            {(field) => (
              <field.DatePickerField
                clearable
                label="End Date"
                placeholder="Select end date..."
              />
            )}
          </form.AppField>
        </div>
      </form.AppForm>
    </Modal>
  );
};
